import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Copy, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { AppCard } from '../../components/ui/AppCard';
import { NicknameField } from '../../components/integrations/NicknameField';
import { CredentialsCard, CREDENTIAL_SOURCE_TYPES } from '../../components/integrations/CredentialsCard';
import { resolveNickname } from '../../lib/mappingNickname';
import { TextField, TextArea } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { ConfidenceBadge } from '../../components/reports/ConfidenceBadge';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { uid, type Mapping, type Template } from '../../types';
import {
  LimitError,
  fetchSignals,
  fetchTemplate,
  recordMappingFieldDecisions,
  fetchSampleFromUrl,
  searchTemplates,
  suggestMappingFieldsFromAI,
  suggestMappingFieldsFromMemory,
  type FieldDecision,
  type MemorySuggestion,
} from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import {
  AUTO_FORMATS,
  AUTO_TYPES,
  SUGGEST_THRESHOLD,
  detectAutoFormat,
  generateMapping,
  generatePromMapping,
  matchExistingSignalName,
  parseAutoSample,
  runnableSourceType,
  webhookBodyFormat,
  unsupportedReason,
  confidenceLevel,
  type AutoFormat,
  type AutoType,
} from '../../lib/autoMapping';
import { resolvePath, type MappingRule, type MappingSpec } from '../../lib/mappingEngine';
import { previewMapping } from '../../lib/mappingPreview';
import { flattenPaths, getOutputs, setOutputRules, summarizePreview } from '../../lib/mappingSpecEdit';
import { applyOverlays, distinctValues, effectiveSplitMap, type OutputOverlay } from '../../lib/signalSplit';
import { useChannelTemplates } from '../../lib/useChannelTemplates';
import { describeMappingRules } from '../../lib/mappingFieldTable';
import { buildSignalTemplate, COLORS, newSignalNames, producedSignalNames } from '../../lib/signalTemplate';

const MAX_SAMPLE_BYTES = 2 * 1024 * 1024;
const EXT_FORMAT: Record<string, AutoFormat> = { json: 'json', csv: 'csv', tsv: 'tsv', xml: 'xml', yaml: 'yaml', yml: 'yaml', prom: 'prometheus' };
const PLACEHOLDER = `Paste a few records - or just the field names:

{"host": "web-1", "cpu_pct": 42.5, "ts": "2026-09-19T14:03:21Z"}

host, cpu_pct, timestamp`;

const KEEP_CONST = '__const__';
// A picked value "const:<text>" sets the rule to that constant text instead of a sample path.
const CONST_PREFIX = 'const:';
const DROP = '__drop__'; // "don't map this" - the rule is removed

// What each Signal field means, in plain words (the mapping's own keys are `name`, `value`, `at`, `labels.*`).
export function fieldLabel(to: string): { title: string; hint: string } {
  if (to === 'name') return { title: 'Signal name', hint: 'what the Signal is called' };
  if (to === 'signalRef') return { title: 'Signal schema', hint: 'which existing channel signal this targets' };
  if (to === 'value') return { title: 'Value', hint: 'the number it carries' };
  if (to === 'at') return { title: 'Time', hint: 'when it happened' };
  if (to.startsWith('labels.')) return { title: `Label · ${to.slice(7)}`, hint: 'extra detail to filter and group by' };
  return { title: to, hint: '' };
}

// A user's dropdown picks, keyed "<output>:<rule>" -> a sample path (or
// KEEP_CONST to leave a generated constant alone). Applied over the generated
// rules; only `from` changes, so cast/timestamp/etc. stay as generated.
function applyPicks(spec: MappingSpec, picks: Record<string, string>, extraLabels: string[] = []): MappingSpec {
  let out = spec;
  getOutputs(spec).forEach((rules, o) => {
    const kept = rules.filter((_, i) => picks[`${o}:${i}`] !== DROP);
    const mapped = rules.map((r, i): MappingRule => {
      const pick = picks[`${o}:${i}`];
      if (!pick || pick === KEEP_CONST || pick === DROP) return r;
      if (pick.startsWith(CONST_PREFIX)) return { ...r, const: pick.slice(CONST_PREFIX.length) };
      const { const: _drop, ...rest } = r;
      return { ...rest, from: pick };
    });
    const have = new Set(kept.map((r) => r.to));
    const added = extraLabels
      .map((path): MappingRule => ({ to: `labels.${path.replace(/[^A-Za-z0-9_]/g, '_').replace(/^_+|_+$/g, '') || 'field'}`, from: path }))
      .filter((r) => !have.has(r.to));
    out = setOutputRules(out, o, [...mapped.filter((_, i) => picks[`${o}:${i}`] !== DROP), ...added]);
  });
  return out;
}

const selCls = 'rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-1.5 py-0.5 text-[12px] outline-none focus:border-blue-500 max-w-[14rem]';

// The main "Signal" control of a rule set: pin the output to one of the
// destination channel's template Signals, or split it by a field so each value
// of that field (e.g. type: cpu / ram / hdd) becomes its own Signal.
function SignalPicker({
  label,
  overlay,
  groups,
  signalNames,
  candidates,
  records,
  onChange,
}: {
  label: string;
  overlay: OutputOverlay;
  groups: { title: string; signals: { name: string; emoji: string }[] }[];
  signalNames: string[];
  candidates: string[];
  records: import('../../lib/mappingEngine').JsonValue[];
  onChange: (ov: OutputOverlay) => void;
}) {
  const values = useMemo(() => (overlay.splitBy ? distinctValues(records, overlay.splitBy) : []), [overlay.splitBy, records]);
  const effective = effectiveSplitMap(values, signalNames, overlay.splitMap);
  const options = (
    <>
      {groups.map((g) => (
        <optgroup key={g.title} label={g.title}>
          {g.signals.map((sg, i) => (
            <option key={`${sg.name}-${i}`} value={sg.name}>
              {sg.emoji} {sg.name}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-black/[0.03] dark:bg-white/[0.05] p-2">
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        <label className="flex items-center gap-1.5 font-medium">
          {label}
          <select
            aria-label={label}
            value={overlay.splitBy ? '' : (overlay.signal ?? '')}
            disabled={!!overlay.splitBy}
            onChange={(e) => onChange({ signal: e.target.value || undefined })}
            className={selCls}
          >
            <option value="">{overlay.splitBy ? 'Chosen per value ↓' : 'Auto (from the data)'}</option>
            {options}
          </select>
        </label>
        {candidates.length > 0 && (
          <label className="flex items-center gap-1.5 font-medium">
            Split by
            <select
              aria-label="Split by"
              value={overlay.splitBy ?? ''}
              onChange={(e) => onChange(e.target.value ? { splitBy: e.target.value } : {})}
              className={selCls}
            >
              <option value="">(no split)</option>
              {candidates.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        )}
        {groups.length === 0 && <span className="text-black/45 dark:text-white/45">This channel has no schema Signals yet - names come from your data.</span>}
      </div>
      {overlay.splitBy && (
        <div className="flex flex-col gap-1">
          {values.length === 0 && <span className="text-[11px] text-black/45 dark:text-white/45">No example values for “{overlay.splitBy}” yet.</span>}
          {values.map((v) => (
            <label key={v} className="flex items-center gap-1.5 text-[12px]">
              <span className="font-mono min-w-[5rem]">“{v}” →</span>
              <select
                aria-label={`Signal for ${v}`}
                value={effective[v] ?? ''}
                onChange={(e) => onChange({ ...overlay, splitMap: { ...overlay.splitMap, [v]: e.target.value } })}
                className={selCls}
              >
                <option value="">Keep “{v}” as the name</option>
                {options}
              </select>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const roleStyle: Record<string, string> = {
  time: 'text-blue-600 dark:text-blue-400',
  name: 'text-violet-600 dark:text-violet-400',
  value: 'text-emerald-600 dark:text-emerald-400',
  label: 'text-black/60 dark:text-white/60',
  skipped: 'text-black/35 dark:text-white/35',
};

// Auto-mapping (Browse connectors -> "Auto-map from an example"): example
// data (rows, or only field names) -> type -> channel -> a generated Mapping,
// previewed against the example before anything is created. Generation is
// lib/autoMapping.ts; evaluation is the parity-tested mapping engine, so the
// preview shown here is what ingestion will do. Format x type combinations
// nothing can ingest yet still generate + preview, but Create is withheld and
// says why (unsupportedReason) rather than making a dead integration.
export function AutoMappingScreen() {
  const navigate = useNavigate();
  const { channels, isChannelOwner, addMapping, addTemplate, updateTemplate, updateChannel, profile, aiSettings } = useStore();
  const ownedChannels = channels.filter(isChannelOwner);

  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [sampleChannel, setSampleChannel] = useState(channels[0]?.nickname ?? '');
  const [sampleCount, setSampleCount] = useState('20');
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState('');
  const [formatChoice, setFormatChoice] = useState<'auto' | AutoFormat>('auto');
  const [type, setType] = useState<AutoType>('webhook');
  const [channelNickname, setChannelNickname] = useState(ownedChannels[0]?.nickname ?? '');
  const [target, setTarget] = useState('');
  const [intervalSec, setIntervalSec] = useState('60');
  // "Fetch data" (Scraper): the example came from the URL, reduced to one list of records - the saved mapping must carry that path.
  const [fetched, setFetched] = useState<{ path: string; candidates: { path: string; len: number }[]; bytes: number } | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [title, setTitle] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [limitMessage, setLimitMessage] = useState('');
  const [created, setCreated] = useState<Mapping | null>(null);
  const [copied, setCopied] = useState(false);
  const [extraLabels, setExtraLabels] = useState<{ key: string; list: string[] }>({ key: '', list: [] });
  const [tplTitle, setTplTitle] = useState('');
  const [tplPicked, setTplPicked] = useState<Record<string, boolean>>({});
  const [tplState, setTplState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [tplError, setTplError] = useState('');
  // Stage 3 of the mapping vision: the new Signals this mapping produces
  // either land in an existing template (found by search, same pattern as
  // SaveAsSignalSheet) or seed a brand-new one - never only the latter.
  const [tplTarget, setTplTarget] = useState<'existing' | 'new'>('new');
  const [tplQuery, setTplQuery] = useState('');
  const [tplResults, setTplResults] = useState<Template[]>([]);
  const [tplSearching, setTplSearching] = useState(false);
  const [tplSelected, setTplSelected] = useState<Template | null>(null);
  const tplRequestId = useRef(0);
  const [picks, setPicks] = useState<{ key: string; map: Record<string, string> }>({ key: '', map: {} });
  const [overlays, setOverlays] = useState<{ key: string; byOutput: Record<number, OutputOverlay> }>({ key: '', byOutput: {} });

  const detected = useMemo<AutoFormat | null>(() => (text.trim() ? detectAutoFormat(text) : null), [text]);
  const format: AutoFormat | null = formatChoice === 'auto' ? detected : formatChoice;

  const parsed = useMemo(() => {
    if (!format || !text.trim()) return { sample: null, failure: '' };
    try {
      return { sample: parseAutoSample(format, text, fetched?.path || undefined), failure: '' };
    } catch (e) {
      return { sample: null, failure: e instanceof Error ? e.message : 'Could not read that example.' };
    }
  }, [format, text, fetched]);

  const generated = useMemo(() => {
    const s = parsed.sample;
    if (!s || s.records.length === 0) return null;
    return s.prom ? generatePromMapping(s.records) : generateMapping(s.records, s.fieldsOnly);
  }, [parsed.sample]);

  // Mapping memory (Phase 3 - a historical-match bias from this account's own
  // past confirmed decisions) and the LLM-assisted fallback (Phase 4, for
  // fields the heuristic itself scored low-confidence). Both are best-effort
  // additions to what generateMapping already decided - see autoMapping.ts's
  // FieldReport.confidence, which this screen never overrides, only annotates.
  const fieldPaths = useMemo(() => (generated ? generated.fields.filter((f) => f.role !== 'skipped').map((f) => f.path) : []), [generated]);
  const [memorySuggestions, setMemorySuggestions] = useState<Record<string, MemorySuggestion>>({});
  useEffect(() => {
    let cancelled = false;
    if (fieldPaths.length === 0) {
      setMemorySuggestions({});
      return;
    }
    void suggestMappingFieldsFromMemory(fieldPaths).then((s) => {
      if (!cancelled) setMemorySuggestions(s);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldPaths.join('\n')]);

  const lowConfidenceFields = useMemo(() => (generated ? generated.fields.filter((f) => f.role !== 'skipped' && f.confidence < SUGGEST_THRESHOLD) : []), [generated]);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, { to: string; cast?: string; unitScale?: number; timestamp?: string }>>({});
  const [aiState, setAiState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [aiError, setAiError] = useState('');
  const askAI = async () => {
    if (lowConfidenceFields.length === 0 || !parsed.sample) return;
    setAiState('loading');
    setAiError('');
    const result = await suggestMappingFieldsFromAI(aiSettings.provider, aiSettings.apiKeys[aiSettings.provider] ?? '', lowConfidenceFields.map((f) => f.path), parsed.sample.records[0] ?? {});
    if (Object.keys(result.suggestions).length === 0 && result.error) {
      setAiState('error');
      setAiError(result.error);
      return;
    }
    setAiSuggestions((p) => ({ ...p, ...result.suggestions }));
    setAiState('idle');
  };

  // Every template this account owns (searched, same pattern as
  // SaveAsSignalSheet) - not just the ones already attached to the
  // destination channel. The "Signal" / "Signal for <value>" dropdowns
  // below need the WHOLE library so a produced Signal can be matched or
  // picked against any existing Signal you have, not only ones this one
  // channel happens to already reference (stage 3 of the mapping vision).
  const [ownedTemplates, setOwnedTemplates] = useState<Template[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Not routed through queryClient - that cache is keyed identically
        // to the "Add to a template" card's own search below, and this
        // fetch (a fixed, empty-query "everything I own") would otherwise
        // collide with and shadow that search's differently-queried results.
        const page = await searchTemplates('mine', '');
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter((t): t is Template => t !== null);
        if (!cancelled) setOwnedTemplates(hydrated);
      } catch {
        if (!cancelled) setOwnedTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The channel's already-attached templates (fresher/cached) plus every
  // other owned template, deduped by nickname so an attached one isn't shown twice.
  const channelTemplates = useChannelTemplates(channels.find((c) => c.nickname === channelNickname));
  const allTemplates = useMemo(() => {
    const byNickname = new Map(ownedTemplates.map((t) => [t.nickname, t]));
    for (const t of channelTemplates) byNickname.set(t.nickname, t);
    return [...byNickname.values()];
  }, [ownedTemplates, channelTemplates]);

  const signalGroups = useMemo(
    () => allTemplates.map((t) => ({ title: t.title || t.nickname, signals: t.signals.map((sg) => ({ name: sg.name, emoji: sg.emoji })) })),
    [allTemplates],
  );
  const signalNames = useMemo(() => [...new Set(signalGroups.flatMap((g) => g.signals.map((sg) => sg.name)))], [signalGroups]);
  // name -> the signal's own global number, for applyOverlays' signalRef
  // rewrite (Phase 3 of docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md) -
  // a signal predating the number-assignment migration (or one loaded
  // before its template ever saved) has none, so this is deliberately a
  // partial map, not a fallback to some sentinel.
  const signalRefs = useMemo(() => {
    const out: Record<string, string> = {};
    for (const t of allTemplates) for (const sg of t.signals) if (sg.number) out[sg.name] = sg.number;
    return out;
  }, [allTemplates]);
  // name (lowercased) -> the template it lives in, so `create()` can attach
  // that template to the destination channel when a picked Signal comes
  // from one that isn't wired to this channel yet.
  const templateForSignalName = useMemo(() => {
    const out: Record<string, string> = {};
    for (const t of allTemplates) for (const sg of t.signals) if (!(sg.name.toLowerCase() in out)) out[sg.name.toLowerCase()] = t.nickname;
    return out;
  }, [allTemplates]);

  const genKey = useMemo(() => (generated ? JSON.stringify(generated.spec) : ''), [generated]);
  const activeOverlays = useMemo(() => (overlays.key === genKey ? overlays.byOutput : {}), [overlays, genKey]);
  // A default "Signal" pick for each output, guessed by matching its
  // generated name against the destination channel's existing template
  // Signals - e.g. a "cpu_pct" field lands on an existing "cpu_usage" Signal
  // instead of minting a near-duplicate. Only a fallback: activeOverlays
  // (the user's own picks, including explicitly choosing "Auto") wins.
  const suggestedOverlays = useMemo(() => {
    const out: Record<number, OutputOverlay> = {};
    if (!generated || signalNames.length === 0) return out;
    getOutputs(generated.spec).forEach((rules, o) => {
      const nameRule = rules.find((r) => r.to === 'name');
      const candidate = typeof nameRule?.const === 'string' ? nameRule.const : undefined;
      if (!candidate) return;
      const match = matchExistingSignalName(candidate, signalNames);
      if (match) out[o] = { signal: match };
    });
    return out;
  }, [generated, signalNames]);
  // Picks are keyed by rule position, which an overlay can shift - so they reset when either changes.
  const pickKey = genKey + JSON.stringify(activeOverlays);
  const sampleRecords = parsed.sample?.records;

  const sourceType = format ? runnableSourceType(format, type) : undefined;
  const isProm = format === 'prometheus';

  const effectiveOverlays = useMemo(() => ({ ...suggestedOverlays, ...activeOverlays }), [suggestedOverlays, activeOverlays]);

  // Every Signal name this mapping's overlays actually resolve to - a direct
  // pick, or every mapped value of a "Split by" - so `create()` knows which
  // (possibly not-yet-attached) owned templates to wire onto the channel.
  const usedSignalNames = useMemo(() => {
    const names = new Set<string>();
    for (const ov of Object.values(effectiveOverlays)) {
      if (!ov) continue;
      if (ov.signal) names.add(ov.signal);
      if (ov.splitBy) {
        const values = distinctValues(sampleRecords ?? [], ov.splitBy);
        const map = effectiveSplitMap(values, signalNames, ov.splitMap);
        for (const v of Object.values(map)) names.add(v);
      }
    }
    return names;
  }, [effectiveOverlays, sampleRecords, signalNames]);

  const spec: MappingSpec | null = useMemo(() => {
    if (!parsed.sample) return null;
    const channel = channelNickname;
    if (!generated) return null;
    const withOverlays = applyOverlays(generated.spec, effectiveOverlays, { records: parsed.sample.records, signalNames, signalRefs });
    const chosen = applyPicks(withOverlays, picks.key === pickKey ? picks.map : {}, extraLabels.key === genKey ? extraLabels.list : []);
    const recordPath = fetched?.path || parsed.sample.recordPath;
    const base: MappingSpec = { channel, ...(recordPath ? { recordPath } : {}), ...chosen };
    if (type === 'webhook') {
      const bodyFormat = format ? webhookBodyFormat(format) : undefined;
      return { provider: 'generic', ...(bodyFormat ? { format: bodyFormat } : {}), ...base };
    }
    const path = target.trim();
    const withTarget = type === 'file' ? (path.startsWith('file://') || path === '' ? path : `file://${path}`) : path;
    return { target: withTarget, intervalSec: Number(intervalSec) || 60, ...base };
  }, [parsed.sample, generated, channelNickname, target, intervalSec, type, picks, pickKey, effectiveOverlays, signalNames, signalRefs, format, extraLabels, genKey, fetched]);

  // Every path in the example: what a rule's source dropdown offers.
  const samplePaths = useMemo(() => {
    const seen = new Set<string>();
    for (const rec of (parsed.sample?.records ?? []).slice(0, 50)) for (const f of flattenPaths(rec)) seen.add(f.path);
    return [...seen];
  }, [parsed.sample]);

  const pick = (o: number, i: number, value: string) => {
    if (!generated) return;
    setPicks((p) => ({ key: pickKey, map: { ...(p.key === pickKey ? p.map : {}), [`${o}:${i}`]: value } }));
  };

  const setOverlay = (o: number, ov: OutputOverlay) => setOverlays((p) => ({ key: genKey, byOutput: { ...(p.key === genKey ? p.byOutput : {}), [o]: ov } }));
  // Fields whose values can name the Signal: the text-like ones the generator found.
  const splitCandidates = useMemo(() => (generated ? generated.fields.filter((f) => f.role === 'label' || f.role === 'name').map((f) => f.path) : []), [generated]);

  const preview = useMemo(() => {
    const s = parsed.sample;
    if (!s || s.fieldsOnly || !spec || s.records.length === 0) return null;
    const results = previewMapping(spec, s.records, 'sample');
    return { results, summary: summarizePreview(spec, results) };
  }, [parsed.sample, spec]);

  // Signal names this mapping produces that the channel's templates don't offer yet.
  const newNames = useMemo(() => newSignalNames(producedSignalNames(spec, preview?.results ?? null), signalNames), [spec, preview, signalNames]);
  const chosenNames = newNames.filter((n) => tplPicked[n] !== false);
  // Text-input suggestions drawn from the example: field names, distinct values of
  // the name-like fields, and the channel's Signal names.
  const nameSuggestions = useMemo(() => {
    const out = new Set<string>(signalNames);
    for (const f of generated?.fields ?? []) if (f.role === 'name') for (const v of distinctValues(sampleRecords ?? [], f.path, 12)) out.add(v);
    for (const n of newNames) out.add(n);
    return [...out];
  }, [signalNames, generated, sampleRecords, newNames]);
  const titleSuggestions = useMemo(() => {
    const out = new Set<string>();
    if (channelNickname) out.add(`#${channelNickname} ${AUTO_FORMATS.find((f) => f.id === format)?.label ?? ''}`.trim());
    for (const f of generated?.fields ?? []) if (f.role !== 'skipped') out.add(f.path.split('.').pop() ?? f.path);
    return [...out].slice(0, 12);
  }, [channelNickname, format, generated]);

  // Search the account's own templates while "Add to a template" is picked -
  // same debounced-search pattern as SaveAsSignalSheet, so stage 3 can
  // browse every template this account owns, not only the ones already
  // attached to the destination channel.
  useEffect(() => {
    if (tplTarget !== 'existing') return;
    const thisRequest = ++tplRequestId.current;
    const timer = setTimeout(async () => {
      setTplSearching(true);
      try {
        const page = await queryClient.query({
          queryKey: queryKeys.templateSearch('mine', tplQuery, 0),
          queryFn: () => searchTemplates('mine', tplQuery),
        });
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter((t): t is Template => t !== null);
        if (tplRequestId.current === thisRequest) setTplResults(hydrated);
      } catch {
        if (tplRequestId.current === thisRequest) setTplResults([]);
      } finally {
        if (tplRequestId.current === thisRequest) setTplSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tplTarget, tplQuery]);

  // Names in `chosenNames` already present in the selected template - these
  // map onto the existing Signal instead of duplicating it.
  const existingInSelected = useMemo(() => {
    if (!tplSelected) return new Set<string>();
    return new Set(chosenNames.filter((n) => tplSelected.signals.some((s) => s.name.toLowerCase() === n.toLowerCase())));
  }, [tplSelected, chosenNames]);

  const saveSignals = async () => {
    if (chosenNames.length === 0) return;
    if (tplTarget === 'existing' && !tplSelected) return;
    setTplState('saving');
    setTplError('');
    try {
      const ch = channels.find((c) => c.nickname === channelNickname);
      let saved: Template;
      if (tplTarget === 'existing' && tplSelected) {
        const additions = chosenNames.filter((n) => !existingInSelected.has(n));
        saved =
          additions.length === 0
            ? tplSelected
            : {
                ...tplSelected,
                signals: [
                  ...tplSelected.signals,
                  ...additions.map((name, i) => ({ id: uid(), name, colorHex: COLORS[i % COLORS.length], emoji: '⚡️' })),
                ],
              };
        if (additions.length > 0) await updateTemplate(saved);
      } else {
        const nickname = resolveNickname('', '@signals', uid().slice(0, 8));
        saved = buildSignalTemplate(tplTitle.trim() || title.trim() || 'Signals from mapping', nickname, chosenNames, uid, profile?.nickname ?? '');
        await addTemplate(saved);
      }
      if (ch && !(ch.templateIds ?? []).includes(saved.nickname)) {
        await updateChannel({ ...ch, templateIds: [...(ch.templateIds ?? []), saved.nickname] });
      }
      setTplState('done');
    } catch (err) {
      setTplError(err instanceof Error ? err.message : 'Unable to save these signals.');
      setTplState('idle');
    }
  };

  const loadFile = async (file: File | undefined) => {
    setFileError('');
    if (!file) return;
    if (file.size > MAX_SAMPLE_BYTES) return setFileError('That file is over 2 MB - paste a smaller excerpt.');
    setText(await file.text());
    setFileName(file.name);
    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    setFormatChoice(EXT_FORMAT[ext] ?? 'auto');
  };

  // Pulls the last N Signals already sitting in one of the user's channels
  // and drops them into the example box as JSON, so a mapping can be shaped
  // from real recent data instead of hand-typed sample text. Only the fields
  // a mapping could plausibly read from map back out - id/channelId are
  // internal plumbing, not source data.
  const loadSample = async () => {
    if (!sampleChannel) return;
    setSampleLoading(true);
    setSampleError('');
    try {
      const count = Math.min(Math.max(Number(sampleCount) || 20, 1), 200);
      const { signals } = await fetchSignals(sampleChannel, 0, count);
      if (signals.length === 0) {
        setSampleError('That channel has no messages yet.');
        return;
      }
      const records = signals.map((s) => ({
        type: s.type,
        text: s.text,
        sender: s.sender,
        date: s.date,
        ...(s.value !== undefined ? { value: s.value } : {}),
        ...(s.at ? { at: s.at } : {}),
        ...(s.labels && s.labels.length > 0 ? { labels: s.labels } : {}),
      }));
      setText(JSON.stringify(records, null, 2));
      setFileName('');
      setFormatChoice('json');
    } catch (err) {
      setSampleError(err instanceof Error ? err.message : 'Unable to load that channel\'s messages.');
    } finally {
      setSampleLoading(false);
    }
  };

  // Scraper: GET the URL through the server and use the response as the example,
  // then map it field -> field below. `path` re-fetches with another record list.
  const fetchFromUrl = async (path?: string) => {
    setFetching(true);
    setFetchError('');
    const res = await fetchSampleFromUrl(target.trim(), path);
    setFetching(false);
    if (!res.sample) return setFetchError(res.error ?? 'Could not fetch that URL.');
    setText(res.sample.text);
    setFileName('');
    setFormatChoice(res.sample.format === 'json' ? 'json' : 'auto');
    setFetched(res.sample.format === 'json' ? { path: res.sample.recordPath, candidates: res.sample.candidates, bytes: res.sample.bytes } : null);
  };

  const needsTarget = type !== 'webhook';
  const targetOk = !needsTarget || target.trim() !== '';
  const canCreate = !!sourceType && !!spec && (isProm || !!generated) && channelNickname !== '' && title.trim() !== '' && targetOk && !creating;

  const create = async () => {
    if (!canCreate || !spec || !sourceType) return;
    setCreating(true);
    setError('');
    try {
      // A picked Signal (a direct pick or a "Split by" value) may belong to
      // a template you own but haven't attached to this channel yet - wire
      // it in now, otherwise the mapping would reference a Signal the
      // channel has no idea about.
      const ch = channels.find((c) => c.nickname === channelNickname);
      if (ch) {
        const missing = [...usedSignalNames]
          .map((n) => templateForSignalName[n.toLowerCase()])
          .filter((nick): nick is string => !!nick && !(ch.templateIds ?? []).includes(nick));
        if (missing.length > 0) await updateChannel({ ...ch, templateIds: [...(ch.templateIds ?? []), ...new Set(missing)] });
      }
      const saved = await addMapping({
        id: uid(),
        nickname: resolveNickname(nicknameInput, '@auto', uid().slice(0, 8)),
        title: title.trim(),
        description: `${AUTO_FORMATS.find((f) => f.id === format)?.label} ${type} -> #${channelNickname} (auto-mapped)`,
        isPublic: false,
        sourceType,
        spec: spec as Record<string, unknown>,
      });
      setCreated(saved);
      // Best-effort: teach the mapping memory what this integration's
      // fields actually meant, so a later, differently-named source
      // benefits (docs/AUTOMATIC_SEMANTIC_SIGNAL_MAPPING.md's "Mapping
      // Memory"). Never blocks or fails the create that just succeeded.
      const decisions: FieldDecision[] = getOutputs(spec)
        .flatMap((rules) => rules)
        .filter((r): r is MappingRule & { to: string; from: string } => !!r.to && !!r.from)
        .map((r) => ({ name: r.from, to: r.to, cast: r.cast, unitScale: r.unitScale, timestamp: r.timestamp }));
      void recordMappingFieldDecisions(decisions);
    } catch (err) {
      if (err instanceof LimitError) setLimitMessage(err.message);
      else setError(err instanceof Error ? err.message : 'Unable to create this integration.');
    } finally {
      setCreating(false);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      /* clipboard unavailable - the value is on screen */
    }
  };

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Auto-map from an example" backTo="/integrations/catalog" />
        {created.sourceType === 'webhook' ? (
          <FormSection title="Send data here">
            <code className="rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all break-all">{`POST /ingest/webhook/${created.nickname}`}</code>
            <p className="text-xs text-black/45 dark:text-white/45 m-0">
              Copy the token now - it won't be shown again. Send it as <code className="font-mono">Authorization: Bearer &lt;token&gt;</code>, with a body shaped like your example
              (JSON, XML, CSV, TSV, YAML or text lines - the receiver reads it as the format saved with this integration unless the request's
              Content-Type says otherwise).
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all">{created.ingestToken}</code>
              <Button variant="secondary" onClick={() => void copy(created.ingestToken ?? '')}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </FormSection>
        ) : CREDENTIAL_SOURCE_TYPES.includes(created.sourceType) && created.sourceType.startsWith('scrape_') ? (
          <FormSection title="Zmist is polling this now">
            <p className="text-sm text-black/60 dark:text-white/60 m-0">
              The mapping is saved and scheduled - no agent to run. If the source needs a bearer token, basic auth or a custom header, add it below.
            </p>
            <CredentialsCard nickname={created.nickname} />
          </FormSection>
        ) : (
          <FormSection title="Run the agent">
            <p className="text-sm text-black/60 dark:text-white/60 m-0">
              The mapping is saved. On the machine that can reach the source, run{' '}
              <code className="font-mono">cue-cli import --mapping {created.nickname}</code> (or the daemon with <code className="font-mono">--import</code>).
            </p>
          </FormSection>
        )}
        <Badge>Configured</Badge>
        <Button onClick={() => navigate(`/integrations/${created.id}`)} full>
          Open the integration to fine-tune the mapping
        </Button>
      </div>
    );
  }

  const summary = preview?.summary;

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Auto-map from an example" backTo="/integrations/catalog" />
      <UpgradePrompt open={!!limitMessage} onClose={() => setLimitMessage('')} message={limitMessage} title="Connector limit reached" />

      <FormSection title="1 · Your example">
        <p className="text-xs text-black/45 dark:text-white/45 m-0">
          Paste a few real records, or only the field names (comma or line separated). Read in your browser - nothing is sent anywhere until you create the
          integration.
        </p>
        <TextArea
          aria-label="Example input"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setFileName('');
            setFetched(null);
          }}
          placeholder={PLACEHOLDER}
          className="font-mono text-[12px] min-h-[140px]"
          spellCheck={false}
        />
        <div className="flex flex-wrap items-center gap-2">
          <label className="cursor-pointer rounded-lg border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm font-medium hover:bg-black/[0.04] dark:hover:bg-white/[0.06]">
            Load from file…
            <input
              type="file"
              aria-label="Load example from file"
              accept=".json,.csv,.tsv,.xml,.yaml,.yml,.txt,.log,.prom"
              className="sr-only"
              onChange={(e) => {
                void loadFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          {fileName && <span className="text-xs text-black/45 dark:text-white/45">{fileName}</span>}
          <div className="ml-auto w-44">
            <Select aria-label="Format" value={formatChoice} onChange={(e) => setFormatChoice(e.target.value as 'auto' | AutoFormat)}>
              <option value="auto">{detected ? `Auto (${AUTO_FORMATS.find((f) => f.id === detected)?.label})` : 'Auto-detect'}</option>
              {AUTO_FORMATS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        {fileError && <p role="alert" className="text-xs text-red-500 m-0">{fileError}</p>}
        {parsed.failure && <p role="alert" className="text-xs text-red-500 m-0">{parsed.failure}</p>}

        {channels.length > 0 && (
          <div className="flex flex-wrap items-end gap-2 pt-1 border-t border-black/[0.06] dark:border-white/[0.08]">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-black/50 dark:text-white/50 flex-1 min-w-[8rem]">
              …or pull recent messages from a channel
              <Select aria-label="Channel to sample" value={sampleChannel} onChange={(e) => setSampleChannel(e.target.value)}>
                {channels.map((c) => (
                  <option key={c.id} value={c.nickname}>
                    #{c.nickname}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-medium text-black/50 dark:text-white/50 w-20">
              Last
              <TextField aria-label="How many messages" type="number" min={1} max={200} value={sampleCount} onChange={(e) => setSampleCount(e.target.value)} />
            </label>
            <Button variant="secondary" onClick={() => void loadSample()} disabled={!sampleChannel || sampleLoading}>
              {sampleLoading ? <Loader2 size={16} className="animate-spin" /> : null}
              Load as JSON
            </Button>
          </div>
        )}
        {sampleError && <p role="alert" className="text-xs text-red-500 m-0">{sampleError}</p>}
      </FormSection>

      <FormSection title="2 · How the data arrives">
        <div role="radiogroup" aria-label="Source type" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {AUTO_TYPES.map((t) => {
            const ok = format ? !!runnableSourceType(format, t.id) : true;
            return (
              <button
                key={t.id}
                role="radio"
                aria-checked={type === t.id}
                onClick={() => setType(t.id)}
                className={`rounded-2xl border p-3 text-left transition-colors ${
                  type === t.id ? 'border-blue-500 bg-blue-600/5' : 'border-black/10 dark:border-white/10 hover:bg-black/[0.03] dark:hover:bg-white/[0.05]'
                }`}
              >
                <span className="font-semibold text-sm">{t.label}</span>
                <span className="block text-xs text-black/50 dark:text-white/50">{t.hint}</span>
                {!ok && <span className="block mt-1 text-[11px] text-amber-600 dark:text-amber-400">Preview only for this format</span>}
              </button>
            );
          })}
        </div>
        {format && !sourceType && <p role="status" className="text-xs text-amber-600 dark:text-amber-400 m-0">{unsupportedReason(format, type)}</p>}
        {needsTarget && (
          <div className="grid gap-2 sm:grid-cols-[1fr_8rem]">
            <TextField
              label={type === 'file' ? 'File path on the agent machine' : type === 'scraper' ? 'URL Zmist polls' : 'URL the agent polls'}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={type === 'file' ? '/var/log/app/metrics.json' : type === 'scraper' ? 'https://example.com/metrics' : 'http://127.0.0.1:9100/metrics'}
              spellCheck={false}
            />
            <TextField label="Every (sec)" type="number" min={1} value={intervalSec} onChange={(e) => setIntervalSec(e.target.value)} />
          </div>
        )}
        {type === 'scraper' && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => void fetchFromUrl()} disabled={fetching || !/^https?:\/\//i.test(target.trim())}>
                {fetching ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                Fetch data from this URL
              </Button>
              <span className="text-xs text-black/45 dark:text-white/45">
                Zmist fetches it once, puts the real data in the example above, and you map each field below.
              </span>
            </div>
            {fetchError && <p role="alert" className="text-xs text-red-500 m-0">{fetchError}</p>}
            {fetched && fetched.candidates.length > 1 && (
              <label className="flex flex-col gap-1 text-[11px] font-medium text-black/50 dark:text-white/50">
                The response holds several lists - which one are the records?
                <Select aria-label="Record list" value={fetched.path} disabled={fetching} onChange={(e) => void fetchFromUrl(e.target.value)}>
                  {fetched.candidates.map((c) => (
                    <option key={c.path} value={c.path}>
                      {c.path} ({c.len})
                    </option>
                  ))}
                </Select>
              </label>
            )}
            {fetched?.path && (
              <p className="text-xs text-black/45 dark:text-white/45 m-0">
                Records come from <code className="font-mono">{fetched.path}</code> in the response - saved with the mapping so every poll reads the same list.
              </p>
            )}
          </div>
        )}
        {type === 'scraper' && (
          <p className="text-xs text-black/45 dark:text-white/45 m-0">
            Zmist's own servers fetch this URL, so it must be reachable from the public internet - not a localhost or private address only your
            machine can reach. Need auth headers? Add them under "Scrape credentials" after creating this integration.
          </p>
        )}
      </FormSection>

      <FormSection title="3 · Where the Signals go">
        {ownedChannels.length === 0 ? (
          <p className="text-sm text-black/60 dark:text-white/60 m-0">
            You don't own a channel yet. <Link to="/channels/new" className="underline">Create one</Link> first.
          </p>
        ) : (
          <Select aria-label="Destination channel" value={channelNickname} onChange={(e) => setChannelNickname(e.target.value)}>
            {ownedChannels.map((c) => (
              <option key={c.id} value={c.nickname}>
                #{c.nickname}
              </option>
            ))}
          </Select>
        )}
      </FormSection>

      <FormSection title="4 · Generated mapping">
        {!parsed.sample ? (
          <p className="text-sm text-black/45 dark:text-white/45 m-0 flex items-center gap-1.5">
            <Wand2 size={14} /> Add an example above and the mapping appears here.
          </p>
        ) : !generated ? (
          <p className="text-sm text-black/45 dark:text-white/45 m-0">No records found in that example.</p>
        ) : (
          <>
            {isProm && (
              <p className="text-xs text-black/50 dark:text-white/50 m-0">
                {parsed.sample.prom?.length ?? 0} samples · {new Set(parsed.sample.prom?.map((p) => p.name)).size} metrics found. Each sample is a record - <code className="font-mono">name</code>, <code className="font-mono">value</code> and <code className="font-mono">labels.*</code> - so any field can feed any Signal field. Narrow it later with a metric allowlist.
              </p>
            )}
            {generated.notes.length > 0 && (
              <ul className="m-0 pl-4 text-xs text-amber-700 dark:text-amber-400 flex flex-col gap-0.5">
                {generated.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
            {spec && (
              <AppCard compact className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">Map your fields to the Signal</span>
                <p className="text-xs text-black/50 dark:text-white/50 m-0">
                  Each row is one part of the Signal Zmist will create. Choose which field of your example fills it - the value shown is from your first record.
                </p>
                {describeMappingRules(spec).map((o) => (
                  <div key={o.outputIndex} className="flex flex-col gap-1.5">
                    {spec.outputs && <span className="text-[11px] text-black/40 dark:text-white/40">Signal {o.outputIndex + 1}</span>}
                    <SignalPicker
                      label={spec.outputs ? `Signal name (output ${o.outputIndex + 1})` : 'Signal name'}
                      overlay={activeOverlays[o.outputIndex] ?? suggestedOverlays[o.outputIndex] ?? {}}
                      groups={signalGroups}
                      signalNames={signalNames}
                      candidates={splitCandidates}
                      records={sampleRecords ?? []}
                      onChange={(ov) => setOverlay(o.outputIndex, ov)}
                    />
                    {!activeOverlays[o.outputIndex] && suggestedOverlays[o.outputIndex]?.signal && (
                      <span className="text-[11px] text-blue-600 dark:text-blue-400 -mt-1">
                        Matched to an existing Signal in this channel's schema - change it above if that's wrong.
                      </span>
                    )}
                    {getOutputs(spec)[o.outputIndex].map((rule, i) => {
                      const row = o.rows[i];
                      const current = rule.from ?? KEEP_CONST;
                      const opts = rule.from && !samplePaths.includes(rule.from) ? [rule.from, ...samplePaths] : samplePaths;
                      // An active Signal choice above already sets the name - no second, conflicting row.
                      const ov = activeOverlays[o.outputIndex];
                      if ((rule.to === 'name' || rule.to === 'signalRef') && (ov?.signal || ov?.splitBy)) return null;
                      const fl = fieldLabel(rule.to ?? '');
                      const first = sampleRecords?.[0];
                      const example = rule.from && first !== undefined ? resolvePath(first, rule.from) : rule.const;
                      return (
                        <div key={i} className="grid gap-1 sm:grid-cols-[9rem_1.25rem_minmax(0,1fr)] sm:items-center text-[12px]">
                          <span className="font-medium" title={fl.hint}>
                            {fl.title}
                            <span className="block text-[11px] font-normal text-black/40 dark:text-white/40">{fl.hint}</span>
                          </span>
                          <span aria-hidden className="hidden sm:block">←</span>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <select
                              aria-label={`Source for ${rule.to}`}
                              value={current}
                              onChange={(e) => pick(o.outputIndex, i, e.target.value)}
                              className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-1.5 py-1 text-[12px] font-mono outline-none focus:border-blue-500 max-w-[14rem]"
                            >
                              {rule.const !== undefined && <option value={KEEP_CONST}>fixed text: {String(rule.const)}</option>}
                              {opts.map((p) => (
                                <option key={p} value={p}>
                                  field: {p}
                                </option>
                              ))}
                              {rule.to?.startsWith('labels.') && <option value={DROP}>(don't map this)</option>}
                            </select>
                            {rule.const !== undefined && current === KEEP_CONST && (
                              <input
                                aria-label={`Text for ${rule.to}`}
                                list="auto-name-suggestions"
                                value={String(rule.const)}
                                onChange={(e) => pick(o.outputIndex, i, `${CONST_PREFIX}${e.target.value}`)}
                                className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-1.5 py-1 text-[12px] font-mono outline-none focus:border-blue-500 w-36"
                              />
                            )}
                            {example !== undefined && example !== '' && typeof example !== 'object' && (
                              <span className="text-black/45 dark:text-white/45 font-mono truncate max-w-[10rem]" title="value in your first record">
                                e.g. {String(example)}
                              </span>
                            )}
                            {row?.transform && <span className="text-black/45 dark:text-white/45">· {row.transform}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {samplePaths.length > 0 && (
                  <label className="flex items-center gap-1.5 text-[12px] font-medium">
                    Add a label
                    <select
                      aria-label="Add a label from a field"
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) setExtraLabels((p) => ({ key: genKey, list: [...(p.key === genKey ? p.list : []), v] }));
                      }}
                      className={selCls}
                    >
                      <option value="">Choose a field…</option>
                      {samplePaths.map((p) => (
                        <option key={p} value={p}>
                          field: {p}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </AppCard>
            )}
            <AppCard compact className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">Fields</span>
                {lowConfidenceFields.length > 0 && (
                  <Button variant="secondary" onClick={() => void askAI()} disabled={aiState === 'loading'}>
                    {aiState === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    Ask AI about {lowConfidenceFields.length} unsure field{lowConfidenceFields.length === 1 ? '' : 's'}
                  </Button>
                )}
              </div>
              {aiError && <p role="alert" className="text-xs text-red-500 m-0">{aiError}</p>}
              {generated.fields.map((f) => {
                const memory = memorySuggestions[f.path];
                const ai = aiSuggestions[f.path];
                return (
                  <div key={f.path} className="flex flex-col gap-0.5">
                    <div className="flex justify-between gap-3 text-[12px]">
                      <code className="font-mono truncate">{f.path}</code>
                      <span className={roleStyle[f.role]}>
                        <span className="font-medium">{f.role}</span> · {f.detail}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <ConfidenceBadge level={confidenceLevel(f.confidence)} />
                      {f.alternatives?.map((a, i) => (
                        <span key={i} className="text-[11px] text-black/40 dark:text-white/40">
                          or {a.role} ({Math.round(a.confidence * 100)}%) - {a.detail}
                        </span>
                      ))}
                      {memory && (
                        <span className="text-[11px] text-blue-600 dark:text-blue-400" title="From a mapping decision you confirmed before, on a different integration">
                          history: {memory.to} ({Math.round(memory.score * 100)}%)
                        </span>
                      )}
                      {ai && (
                        <span className="text-[11px] text-violet-600 dark:text-violet-400" title="Suggested by AI - not applied automatically">
                          AI: {ai.to}{ai.unitScale ? ` ×${ai.unitScale}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </AppCard>
            {summary ? (
              <AppCard compact className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">Preview</span>
                <p className="text-xs font-medium m-0" data-testid="auto-summary">
                  {summary.records} record{summary.records === 1 ? '' : 's'} tested · {summary.produced} signal{summary.produced === 1 ? '' : 's'} produced
                  {summary.pendingSignalRef > 0 ? ` · ${summary.pendingSignalRef} targeting a channel signal (resolved when sent)` : ''} ·{' '}
                  {summary.rejected} rejected
                </p>
                {summary.failures.map((f) => (
                  <span key={f} className="text-[12px] text-amber-600 dark:text-amber-400">{f}</span>
                ))}
                <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                  {preview!.results.slice(0, 5).flatMap((r) =>
                    r.outputs.map(
                      (o) =>
                        o.signal && (
                          <div key={`${r.recordIndex}-${o.outputIndex}`} className="flex flex-wrap items-center gap-1">
                            <Badge className="text-emerald-600 dark:text-emerald-400 border-emerald-600/30">{o.signal.name}</Badge>
                            {o.signal.value && <span className="text-[12px] font-mono">{o.signal.value}</span>}
                            <span className="text-[11px] text-black/40 dark:text-white/40">{o.signal.atSynthesized ? 'at: arrival time' : o.signal.at}</span>
                            {o.signal.labels.map((l, k) => (
                              <Badge key={k}>{l}</Badge>
                            ))}
                          </div>
                        ),
                    ),
                  )}
                </div>
              </AppCard>
            ) : (
              parsed.sample.fieldsOnly && <p className="text-xs text-black/45 dark:text-white/45 m-0">No example values, so there is nothing to preview yet.</p>
            )}
          </>
        )}

        <datalist id="auto-name-suggestions">
          {nameSuggestions.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <datalist id="auto-title-suggestions">
          {titleSuggestions.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>

        {spec && generated && newNames.length > 0 && (
          <AppCard compact className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">Signal schema</span>
            <p className="text-xs text-black/50 dark:text-white/50 m-0">
              These Signals come from your mapping. Map them onto an existing schema's Signals, add them as new Signals in a schema you pick, or start a
              fresh schema - either way it's added to #{channelNickname}.
            </p>
            <div className="flex flex-wrap gap-2">
              {newNames.map((n) => (
                <label key={n} className="flex items-center gap-1 text-[12px]">
                  <input type="checkbox" checked={tplPicked[n] !== false} onChange={(e) => setTplPicked((p) => ({ ...p, [n]: e.target.checked }))} />
                  {n}
                  {tplTarget === 'existing' && existingInSelected.has(n) && (
                    <span className="text-[11px] text-blue-600 dark:text-blue-400">(matches an existing Signal)</span>
                  )}
                </label>
              ))}
            </div>

            <SegmentedControl
              options={[
                { value: 'existing' as const, label: 'Add to a schema' },
                { value: 'new' as const, label: 'New schema' },
              ]}
              value={tplTarget}
              onChange={setTplTarget}
            />

            {tplTarget === 'existing' ? (
              <div className="flex flex-col gap-2">
                {tplSelected && (
                  <span className="text-xs text-black/50 dark:text-white/50">
                    Selected: <span className="font-medium text-black/70 dark:text-white/70">{tplSelected.title || tplSelected.nickname}</span>
                  </span>
                )}
                <TextField placeholder="Search my schemas" value={tplQuery} onChange={(e) => setTplQuery(e.target.value)} />
                <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
                  {tplSearching && <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">Searching…</span>}
                  {!tplSearching && tplResults.length === 0 && (
                    <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">
                      {tplQuery ? 'No schemas match that search.' : 'No schemas yet - create one instead.'}
                    </span>
                  )}
                  {!tplSearching &&
                    tplResults.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTplSelected(t)}
                        className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left ${
                          tplSelected?.id === t.id
                            ? 'border-blue-500 bg-blue-500/5'
                            : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'
                        }`}
                      >
                        <span className="w-8 h-8 rounded-full flex items-center justify-center text-base shrink-0" style={{ background: `${t.colorHex}1f` }}>
                          {t.emoji}
                        </span>
                        <span className="flex-1 min-w-0 truncate text-sm font-medium">{t.title || t.nickname}</span>
                        {tplSelected?.id === t.id && <Check size={16} className="text-blue-600 dark:text-blue-400 shrink-0" />}
                      </button>
                    ))}
                </div>
              </div>
            ) : (
              <TextField label="Schema name" value={tplTitle} onChange={(e) => setTplTitle(e.target.value)} placeholder={title || 'Signals from mapping'} />
            )}

            {tplError && <p role="alert" className="text-xs text-red-500 m-0">{tplError}</p>}
            <Button
              variant="secondary"
              onClick={() => void saveSignals()}
              disabled={chosenNames.length === 0 || tplState !== 'idle' || !channelNickname || (tplTarget === 'existing' && !tplSelected)}
            >
              {tplState === 'done'
                ? 'Saved and added to the channel'
                : tplTarget === 'existing'
                  ? `Add ${chosenNames.filter((n) => !existingInSelected.has(n)).length || 0} Signal(s) to ${tplSelected ? tplSelected.title || tplSelected.nickname : 'the selected template'}`
                  : `Create schema (${chosenNames.length}) and add to #${channelNickname}`}
            </Button>
          </AppCard>
        )}

        {spec && (
          <details>
            <summary className="text-xs text-black/45 dark:text-white/45 cursor-pointer select-none">Generated spec (JSON)</summary>
            <pre className="mt-2 rounded-2xl border border-black/[0.06] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.06] p-3 overflow-x-auto text-[12px] font-mono m-0">{JSON.stringify(spec, null, 2)}</pre>
            <Button variant="bordered" className="mt-2" onClick={() => void copy(JSON.stringify(spec, null, 2))}>
              {copied ? 'Copied' : 'Copy spec'}
            </Button>
          </details>
        )}

        {parsed.sample && (
          <>
            <TextField label="Integration name" list="auto-title-suggestions" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Web server metrics" />
            <NicknameField value={nicknameInput} onChange={setNicknameInput} example="@web-metrics" />
            {error && <p role="alert" className="text-sm text-red-500 m-0">{error}</p>}
            <Button onClick={() => void create()} disabled={!canCreate} full>
              {creating ? <Loader2 size={16} className="animate-spin" /> : null}
              {sourceType ? 'Create integration' : 'Not runnable yet - copy the spec instead'}
            </Button>
          </>
        )}
      </FormSection>
    </div>
  );
}
